import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import Breadcrumbs from "../../../../components/BreadCrumbs";
import { UIButton, UIPageFormFooter, UITextField } from "../../../../components/ui";
import {
  clearCompanyRole,
  getCompanyRoleRequest,
  updateCompanyRoleRequest,
} from "../../../../store/CompanyPortal/Roles/actions";
import PermissionsMatrix from "../CreateRole/PermissionsMatrix";
import {
  createEmptyPermissions,
  arePermissionsEqual,
  toPermissionsPayload,
} from "../CreateRole/permissionModules";
import { permissionsArrayToMap } from "../roleMappers";
import { companyPermissionModules } from "../../../../utils/Constants";
import { canEditCompanyModule } from "../../../../utils/Permissions";

import "../CreateRole/CreateRole.scss";
import "./ViewRole.scss";

const ROLES_TAB_PATH = `${ROUTE_PATHS.companyTeamsPermissions}?tab=roles`;

const ViewRole = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const role = useSelector((state) => state.companyRoles.current);
  const canEdit = canEditCompanyModule(
    companyPermissionModules.rolesManagement,
    login,
  );
  const updating = useSelector((state) => state.companyRoles.updating);
  const teamCopy = messages[lang].teamManagement;
  const copy = teamCopy.viewRole;
  const createCopy = teamCopy.createRole;

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [permissions, setPermissions] = useState(createEmptyPermissions);
  const editFromListRef = useRef(Boolean(location.state?.startInEdit));
  const autoEnteredEditRef = useRef(false);

  const goBackToList = useCallback(
    () => history.push(ROLES_TAB_PATH),
    [history],
  );

  useEffect(() => {
    if (id) dispatch(getCompanyRoleRequest({ id }));
    return () => dispatch(clearCompanyRole());
  }, [dispatch, id, lang]);

  useEffect(() => {
    if (!role) return;
    setPermissions(permissionsArrayToMap(role.permissions));
  }, [role]);

  const exitEdit = useCallback(() => {
    if (editFromListRef.current) {
      goBackToList();
      return;
    }
    if (role) {
      setPermissions(permissionsArrayToMap(role.permissions));
      setName(role.name || "");
    }
    setNameTouched(false);
    setEditing(false);
  }, [goBackToList, role]);

  const enterEdit = () => {
    if (role) setName(role.name || "");
    setNameTouched(false);
    setEditing(true);
  };

  useEffect(() => {
    if (
      role &&
      canEdit &&
      location.state?.startInEdit &&
      !autoEnteredEditRef.current
    ) {
      autoEnteredEditRef.current = true;
      enterEdit();
    }
  }, [role, location.state?.startInEdit, canEdit]);

  const roleName = role?.name || "";
  const isSystemRole = Boolean(role?.systemRole);
  const trimmedName = name.trim();
  const showNameError = editing && !isSystemRole && nameTouched && !trimmedName;

  const savedPermissions = useMemo(
    () =>
      role ? permissionsArrayToMap(role.permissions) : createEmptyPermissions(),
    [role],
  );

  const isEditDirty = useMemo(() => {
    if (!editing) return false;
    const nameChanged = !isSystemRole && trimmedName !== roleName;
    const permissionsChanged = !arePermissionsEqual(permissions, savedPermissions);
    return nameChanged || permissionsChanged;
  }, [editing, isSystemRole, trimmedName, roleName, permissions, savedPermissions]);

  const breadcrumbList = useMemo(
    () => [
      { path: ROUTE_PATHS.companyTeamsPermissions, label: teamCopy.title },
      { path: ROLES_TAB_PATH, label: teamCopy.tabs.roles },
      { path: "", label: roleName },
    ],
    [roleName, teamCopy.tabs.roles, teamCopy.title],
  );

  const cancelEdit = () => {
    if (updating) return;
    exitEdit();
  };

  const handleUpdate = () => {
    if (!role || updating || !isEditDirty) {
      if (!isSystemRole) setNameTouched(true);
      return;
    }
    if (!isSystemRole && !trimmedName) {
      setNameTouched(true);
      return;
    }

    const data = {};
    if (!isSystemRole && trimmedName !== roleName) data.name = trimmedName;
    if (!arePermissionsEqual(permissions, savedPermissions)) {
      data.permissions = toPermissionsPayload(permissions);
    }
    if (Object.keys(data).length === 0) return;

    const displayName = isSystemRole ? roleName : trimmedName;

    dispatch(
      updateCompanyRoleRequest({
        id: role.id,
        data,
        successMessage: copy.updateSuccess.replace("{name}", displayName),
        onSuccess: exitEdit,
      }),
    );
  };

  if (!role) {
    // While loading or on 404 the page renders nothing — toast handles errors.
    return null;
  }

  return (
    <div className="team-management create-role view-role">
      <div className="mt-24 view-role__header">
        <div className="view-role__heading">
          <Breadcrumbs list={breadcrumbList} BreadCrumbHasBackIcon={false} />
          <p className="create-role__subtitle">{copy.subtitle}</p>
        </div>
        {!editing && canEdit && (
          <UIButton
            variant="outlined"
            size="large"
            color="primary"
            startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={enterEdit}
          >
            {copy.editPermissions}
          </UIButton>
        )}
      </div>

      <div>
        <UITextField
          label={copy.roleNameLabel}
          name="roleName"
          required={editing && !isSystemRole}
          value={editing && !isSystemRole ? name : roleName}
          placeholder={createCopy.roleNamePlaceholder}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => setNameTouched(true)}
          error={showNameError}
          helperText={showNameError ? createCopy.roleNameRequired : null}
          disabled={!editing || isSystemRole}
          classNames={{ root: "create-role__name-field mb-24" }}
        />

        <PermissionsMatrix
          value={permissions}
          onChange={setPermissions}
          copy={createCopy}
          readOnly={!editing}
        />
      </div>

      {editing && (
        <UIPageFormFooter
          cancelLabel={copy.cancel}
          submitLabel={copy.updatePermissions}
          onCancel={cancelEdit}
          onSubmit={handleUpdate}
          cancelDisabled={updating}
          submitDisabled={
            updating || !isEditDirty || (!isSystemRole && !trimmedName)
          }
          submitLoading={updating}
        />
      )}
    </div>
  );
};

export default ViewRole;
